import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Download, QrCode, Loader2 } from 'lucide-react';
import QRCode from 'react-qr-code';
import html2canvas from 'html2canvas';
import { Procedure } from '../../types';

interface ProcedureQRModalProps {
  isOpen: boolean;
  onClose: () => void;
  procedure: Procedure | null;
}

// 100mm at 300 DPI = ~1181px
const LABEL_SIZE_PX = 1181;
const PADDING = 50;

const ProcedureQRModal: React.FC<ProcedureQRModalProps> = ({ isOpen, onClose, procedure }) => {
  const qrRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);

  if (!procedure) return null;

  const qrUrl = `${window.location.origin}/procedure/${procedure.id}`;
  const departmentName = procedure.department
    ? `${procedure.department.departmentCode} - ${procedure.department.departmentName}`
    : 'Unknown Department';

  const handleDownload = async () => {
    if (!qrRef.current) return;

    try {
      setDownloading(true);
      // Allow a small delay to ensure rendering is complete
      await new Promise(r => setTimeout(r, 150));
      const canvas = await html2canvas(qrRef.current, {
        scale: 1,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        width: LABEL_SIZE_PX,
        height: LABEL_SIZE_PX,
      });

      const image = canvas.toDataURL('image/png');

      const a = document.createElement('a');
      a.href = image;
      const safeName = procedure.procedureName.replace(/[^a-z0-9]/gi, '_').substring(0, 40);
      a.download = `Procedure-QR-${safeName}.png`;
      a.click();
    } catch (error) {
      console.error('Error generating QR code image', error);
    } finally {
      setDownloading(false);
    }
  };

  // Visible preview scale factor
  const previewSize = 360;
  const scale = previewSize / LABEL_SIZE_PX;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg rounded-2xl p-0 overflow-hidden border-gray-200 shadow-2xl">
        <DialogHeader className="pt-6 px-6 pb-2">
          <div className="mx-auto bg-slate-100 w-14 h-14 rounded-full flex items-center justify-center mb-3">
            <QrCode size={28} className="text-slate-700" />
          </div>
          <DialogTitle className="text-center font-bold text-lg text-gray-900">
            Procedure QR Label
          </DialogTitle>
          <p className="text-center text-sm text-gray-500 mt-1">
            Print-ready 100mm × 100mm label for <strong>{procedure.procedureName}</strong>.
          </p>
        </DialogHeader>

        {/* Preview Area */}
        <div className="flex flex-col items-center justify-center py-6 bg-gray-50 border-y border-gray-100">
          <div
            className="relative bg-white shadow-lg border border-gray-200"
            style={{
              width: previewSize,
              height: previewSize,
              overflow: 'hidden',
            }}
          >
            {/* Scaled full-size label for preview */}
            <div
              style={{
                width: LABEL_SIZE_PX,
                height: LABEL_SIZE_PX,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              <LabelContent
                procedureName={procedure.procedureName}
                departmentName={departmentName}
                qrUrl={qrUrl}
              />
            </div>
          </div>

          <p className="mt-4 text-xs font-medium text-gray-400 text-center uppercase tracking-widest">
            Preview — actual size 100mm × 100mm at 300 DPI
          </p>
        </div>

        {/* Hidden full-size element for html2canvas capture */}
        <div
          ref={qrRef}
          style={{
            position: 'absolute',
            left: '-9999px',
            top: '-9999px',
            width: LABEL_SIZE_PX,
            height: LABEL_SIZE_PX,
          }}
        >
          <LabelContent
            procedureName={procedure.procedureName}
            departmentName={departmentName}
            qrUrl={qrUrl}
          />
        </div>

        <div className="p-6 flex justify-center bg-white">
          <Button
            className="rounded-xl font-semibold bg-slate-900 hover:bg-slate-800 text-white w-full h-12 shadow-lg transition-all active:scale-95"
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                Generating PNG...
              </>
            ) : (
              <>
                <Download size={18} className="mr-2" />
                Download QR Label (PNG)
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Extracted label content so we can render it at two different sizes
const LabelContent: React.FC<{
  procedureName: string;
  departmentName: string;
  qrUrl: string;
}> = ({ procedureName, departmentName, qrUrl }) => {
  // Dynamic font sizing based on text length
  const getNameFontSize = () => {
    if (procedureName.length <= 20) return '52px';
    if (procedureName.length <= 35) return '42px';
    if (procedureName.length <= 50) return '34px';
    return '28px';
  };

  const getDeptFontSize = () => {
    if (departmentName.length <= 25) return '30px';
    if (departmentName.length <= 45) return '24px';
    return '20px';
  };

  return (
    <div
      style={{
        width: LABEL_SIZE_PX,
        height: LABEL_SIZE_PX,
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: PADDING,
        boxSizing: 'border-box',
        border: '4px solid #1e293b',
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      {/* Top: Procedure Name + Department */}
      <div
        style={{
          width: '100%',
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            fontSize: getNameFontSize(),
            fontWeight: 800,
            color: '#0f172a',
            lineHeight: 1.25,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: '100%',
          }}
        >
          {procedureName}
        </div>
        <div
          style={{
            fontSize: getDeptFontSize(),
            fontWeight: 600,
            color: '#475569',
            marginTop: '12px',
            lineHeight: 1.3,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: '100%',
          }}
        >
          {departmentName}
        </div>
      </div>

      {/* Center: QR Code — takes remaining space, centered */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          width: '100%',
          minHeight: 0,
          paddingTop: '24px',
          paddingBottom: '24px',
        }}
      >
        <QRCode
          value={qrUrl}
          size={620}
          style={{ height: 'auto', maxWidth: '100%', width: '100%', maxHeight: '100%' }}
          viewBox="0 0 620 620"
          fgColor="#0f172a"
          bgColor="#ffffff"
        />
      </div>

      {/* Bottom: Microcopy */}
      <div
        style={{
          width: '100%',
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            fontSize: '28px',
            fontWeight: 700,
            color: '#334155',
            letterSpacing: '0.04em',
            lineHeight: 1.4,
          }}
        >
          Scan QR Code to Access<br />This Procedure.
        </div>
      </div>
    </div>
  );
};

export default ProcedureQRModal;
